/* Full Feast — Interactive recipe tools (ingredients section)
 *
 * Features:
 *   1. Checkbox list — tap an ingredient to tick it off, with strikethrough.
 *      State persists in localStorage keyed by article handle.
 *   2. Servings multiplier — −/+ buttons scale all numeric quantities in
 *      the ingredient list. Handles whole numbers, decimals, fractions
 *      ("1/2"), and renders scaled values back as fractions where possible
 *      ("½", "¼", "¾", "⅓", "⅔") or decimals (1.5, 2.5).
 *   3. Section header polish — "Tick all" / "Reset" controls + ingredient
 *      counter.
 */
(function () {
  /* Relocate the hero image to sit AFTER the body H1 (the descriptive
   * subtitle), so the reading order is: short hero title → subtitle →
   * image → article body. Runs first because it doesn't depend on the
   * ingredients section being found. */
  function moveHeroImageAfterH1() {
    const heroImg = document.querySelector('.ff-article__hero-image');
    if (!heroImg) return;
    // Find the first H1 inside the rendered article body, NOT the page hero
    const bodyH1 =
      document.querySelector('.ff-article__main .rte h1') ||
      document.querySelector('.ff-article__main .blog-post-content h1') ||
      document.querySelector(".ff-article__main [class*='blog-post-content'] h1");
    if (!bodyH1) return;
    if (heroImg.nextElementSibling === bodyH1.parentElement) return; // already moved
    bodyH1.insertAdjacentElement('afterend', heroImg);
    heroImg.classList.add('ff-article__hero-image--after-h1');
  }
  /* Place "Recipe at a glance" where it earns its keep.
   *
   * On desktop it is the left column, so it must be a direct child of the
   * layout grid. Stacked on a phone that same DOM order puts a tall stats
   * card above the dish photo (photo pushed off screen), and simply
   * re-ordering with flex sends the card to the very bottom, past the
   * method — because the photo lives inside the article body, not
   * alongside it. So on mobile the card moves to sit directly under the
   * photo: title → subtitle → photo → times and servings → method. */
  const MOBILE = '(max-width: 999px)';
  function placeRecipeSidebar() {
    const layout = document.querySelector('.ff-article__layout');
    const sidebar = document.querySelector('.ff-recipe-sidebar');
    if (!layout || !sidebar) return;
    if (window.matchMedia(MOBILE).matches) {
      const hero = document.querySelector('.ff-article__hero-image');
      if (hero && hero.nextElementSibling !== sidebar) {
        hero.insertAdjacentElement('afterend', sidebar);
      }
    } else if (layout.firstElementChild !== sidebar) {
      layout.insertAdjacentElement('afterbegin', sidebar);
    }
  }

  function initLayout() {
    moveHeroImageAfterH1();
    placeRecipeSidebar();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLayout);
  } else {
    initLayout();
  }
  /* Rotating a phone or resizing must not strand the card in the wrong
   * place, so re-run on the breakpoint crossing itself. */
  const mq = window.matchMedia(MOBILE);
  if (mq.addEventListener) {
    mq.addEventListener('change', placeRecipeSidebar);
  } else if (mq.addListener) {
    mq.addListener(placeRecipeSidebar);
  }

  // Try several roots — Shopify themes wrap article content differently
  // depending on theme version. Find whichever container holds the body.
  const ROOT_CANDIDATES = [
    '.ff-article__main .rte',
    '.ff-article__main',
    "main[data-template='article'] .rte",
    "main[data-template='article'] [class*='blog-post-content']",
    "main[data-template='article'] .article",
    "main[data-template='article']"
  ];
  let ROOT = null;
  for (const sel of ROOT_CANDIDATES) {
    const el = document.querySelector(sel);
    if (el && el.querySelector('h2')) { ROOT = el; break; }
  }
  if (!ROOT) {
    console.warn('[FF recipe tools] No article body found');
    return;
  }

  const STORAGE_KEY = 'ff-recipe-checks::' + (location.pathname || '/');

  /* Normalise text so smart quotes don't break matching */
  function norm(s) {
    return (s || '')
      .toLowerCase()
      .replace(/[‘’ʼ]/g, "'")  // curly + modifier apostrophes → straight
      .replace(/[“”]/g, '"')         // curly double → straight
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* ── 1. Locate the ingredients section ──────────────────────── */
  const headings = ROOT.querySelectorAll('h2, h3');
  let ingHeader = null;
  for (const h of headings) {
    const t = norm(h.textContent);
    // Strip apostrophes entirely for matching: "you'll" / "youll" / "you will"
    const tFlat = t.replace(/'/g, '');
    if (
      tFlat.includes('youll need') ||
      t.includes('you will need') ||
      t.includes('ingredient') ||
      tFlat.includes('what youll need') ||
      tFlat.includes('what you need') ||
      tFlat.includes('what you ll need')
    ) {
      ingHeader = h;
      break;
    }
  }
  if (!ingHeader) {
    console.warn('[FF recipe tools] No "ingredients" heading found. Headings seen:',
      Array.from(headings).map((h) => h.textContent));
    return;
  }

  // Collect all elements between ingHeader and the next h2 (the "How I make it" heading)
  const items = [];
  let n = ingHeader.nextElementSibling;
  while (n && n.tagName !== 'H2') {
    items.push(n);
    n = n.nextElementSibling;
  }
  const lists = items.filter((el) => el.tagName === 'UL');
  if (!lists.length) return;

  /* ── 2. Read servings from tags / sidebar ───────────────────── */
  let defaultServings = 4;
  const sidebar = document.querySelector('.ff-recipe-glance__list');
  if (sidebar) {
    const rows = sidebar.querySelectorAll('.ff-recipe-glance__row');
    for (const row of rows) {
      const label = (row.querySelector('dt')?.textContent || '').toLowerCase();
      if (label.includes('serves')) {
        const v = parseInt((row.querySelector('dd')?.textContent || '').trim(), 10);
        if (!isNaN(v) && v > 0) defaultServings = v;
        break;
      }
    }
  }

  /* ── 3. Quantity parsing + display ──────────────────────────── */
  const FRAC_GLYPHS = { '½': 0.5, '¼': 0.25, '¾': 0.75, '⅓': 1/3, '⅔': 2/3, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875 };
  // Matches: 1, 1.5, 1/2, 1 1/2 (mixed), and unicode fractions
  const QTY_RE = /^(\s*)(\d+\s+\d+\/\d+|\d+\/\d+|\d+\.\d+|\d+|[½¼¾⅓⅔⅛⅜⅝⅞])(\s|\b)/;

  function parseQty(token) {
    token = token.trim();
    if (FRAC_GLYPHS[token] !== undefined) return FRAC_GLYPHS[token];
    if (token.includes(' ')) {
      // mixed e.g. "1 1/2"
      const [whole, frac] = token.split(/\s+/);
      const [num, denom] = frac.split('/').map(Number);
      return parseFloat(whole) + (num / denom);
    }
    if (token.includes('/')) {
      const [num, denom] = token.split('/').map(Number);
      return num / denom;
    }
    return parseFloat(token);
  }

  function formatQty(value) {
    if (!isFinite(value) || value <= 0) return '';
    // Round to 2 decimals to avoid floating point noise
    value = Math.round(value * 100) / 100;
    const whole = Math.floor(value);
    const frac = +(value - whole).toFixed(2);
    // Try to render the fractional part as a unicode glyph
    const fracMap = { 0.25: '¼', 0.5: '½', 0.75: '¾', 0.33: '⅓', 0.34: '⅓', 0.67: '⅔', 0.66: '⅔', 0.125: '⅛', 0.375: '⅜', 0.625: '⅝', 0.875: '⅞' };
    const fracGlyph = fracMap[frac];
    if (frac === 0) return String(whole);
    if (fracGlyph) return whole > 0 ? `${whole} ${fracGlyph}` : fracGlyph;
    // Otherwise show decimal
    return value % 1 === 0 ? String(whole) : value.toString();
  }

  /* ── 4. Render the controls header above the first list ────── */
  const controlsBar = document.createElement('div');
  controlsBar.className = 'ff-ing-controls';
  const minusIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="12" x2="18" y2="12"/></svg>`;
  const plusIcon  = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="6" x2="12" y2="18"/><line x1="6" y1="12" x2="18" y2="12"/></svg>`;
  const printIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>`;
  controlsBar.innerHTML = `
    <div class="ff-ing-controls__servings">
      <span class="ff-ing-controls__label">${(window.FF_I18N || {}).servings || 'Servings'}</span>
      <div class="ff-ing-controls__stepper">
        <button type="button" class="ff-ing-controls__btn" data-action="decrease" aria-label="${(window.FF_I18N || {}).decrease_servings || 'Decrease servings'}">${minusIcon}</button>
        <span class="ff-ing-controls__value" data-servings>${defaultServings}</span>
        <button type="button" class="ff-ing-controls__btn" data-action="increase" aria-label="${(window.FF_I18N || {}).increase_servings || 'Increase servings'}">${plusIcon}</button>
      </div>
      <button type="button" class="ff-ing-controls__reset" data-action="reset" title="${(window.FF_I18N || {}).reset_title || 'Reset to original'}">${(window.FF_I18N || {}).reset || 'Reset'}</button>
    </div>
    <div class="ff-ing-controls__actions">
      <div class="ff-ing-controls__progress" data-progress aria-live="polite">
        <span data-checked>0</span>/<span data-total>0</span> ${(window.FF_I18N || {}).ticked || 'ticked'}
      </div>
      <button type="button" class="ff-ing-controls__print" data-action="print" title="${(window.FF_I18N || {}).print_title || 'Print this recipe'}" aria-label="${(window.FF_I18N || {}).print_aria || 'Print recipe'}">
        ${printIcon}<span>${(window.FF_I18N || {}).print || 'Print'}</span>
      </button>
    </div>
  `;
  lists[0].parentNode.insertBefore(controlsBar, lists[0]);

  /* Print button — triggers the browser print dialog. The @media print
     CSS rules in article-ff.css strip away navigation, sidebar, hero
     image and other chrome so what comes out of the printer is a clean
     recipe card. */
  const printBtn = controlsBar.querySelector('[data-action="print"]');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      document.body.classList.add('ff-printing');
      // Give the browser a tick to apply the class before opening the dialog
      requestAnimationFrame(() => {
        window.print();
        // Clean up after the print dialog closes
        setTimeout(() => document.body.classList.remove('ff-printing'), 500);
      });
    });
  }

  /* ── 5. Convert each ingredient into an interactive checkbox row ── */
  let allItems = [];
  lists.forEach((ul) => {
    ul.classList.add('ff-ing-list');
    ul.querySelectorAll(':scope > li').forEach((li, idx) => {
      // Wrap original quantity in a span we can update
      const html = li.innerHTML;
      const match = html.match(QTY_RE);
      if (match) {
        const qty = match[2];
        const original = parseQty(qty);
        if (isFinite(original) && original > 0) {
          const replaced = html.replace(QTY_RE,
            `$1<span class="ff-ing-qty" data-original="${original}">${qty}</span>$3`);
          li.innerHTML = replaced;
        }
      }

      // Wrap content in label + checkbox
      const inner = li.innerHTML;
      li.classList.add('ff-ing-item');
      li.innerHTML = `
        <button type="button" class="ff-ing-check" aria-label="${(window.FF_I18N || {}).mark_gathered || 'Mark as gathered'}" aria-pressed="false">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
        <span class="ff-ing-text">${inner}</span>
      `;
      allItems.push(li);
    });
  });

  /* ── 6. Persist tick state in localStorage ───────────────────── */
  const total = allItems.length;
  const checkedEl = controlsBar.querySelector('[data-checked]');
  const totalEl = controlsBar.querySelector('[data-total]');
  totalEl.textContent = String(total);

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      return JSON.parse(raw);
    } catch { return {}; }
  }
  function saveState(state) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
  }
  const state = loadState();
  let checkedCount = 0;

  function applyCheckedState() {
    checkedCount = 0;
    allItems.forEach((li, i) => {
      const isChecked = !!state[i];
      li.classList.toggle('is-checked', isChecked);
      const btn = li.querySelector('.ff-ing-check');
      if (btn) btn.setAttribute('aria-pressed', String(isChecked));
      if (isChecked) checkedCount++;
    });
    checkedEl.textContent = String(checkedCount);
  }
  applyCheckedState();

  allItems.forEach((li, i) => {
    const btn = li.querySelector('.ff-ing-check');
    btn.addEventListener('click', () => {
      state[i] = !state[i];
      saveState(state);
      applyCheckedState();
    });
    // Allow clicking text to also toggle
    li.querySelector('.ff-ing-text').addEventListener('click', (e) => {
      // Don't toggle if clicking a link inside the text
      if (e.target.closest('a')) return;
      btn.click();
    });
  });

  /* ── 7. Servings multiplier wiring ───────────────────────────── */
  const valueEl = controlsBar.querySelector('[data-servings]');
  let currentServings = defaultServings;

  function rescale() {
    const factor = currentServings / defaultServings;
    valueEl.textContent = String(currentServings);
    allItems.forEach((li) => {
      const span = li.querySelector('.ff-ing-qty');
      if (!span) return;
      const original = parseFloat(span.dataset.original);
      if (!isFinite(original)) return;
      const scaled = original * factor;
      span.textContent = formatQty(scaled);
    });
  }

  controlsBar.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.action;
      if (action === 'increase') currentServings = Math.min(currentServings + 1, 99);
      else if (action === 'decrease') currentServings = Math.max(currentServings - 1, 1);
      else if (action === 'reset') currentServings = defaultServings;
      rescale();
    });
  });

  /* ── 8. Inline featured-sauce thumbnail on matching ingredient ── */
  // Find FF sauce mentions in li.text and inject a tiny product image
  const sauceLookups = {
    'caribbean hot sauce': 'caribbean-hot-sauce',
    'exotic caribbean sauce': 'exotic-caribbean-sauce',
    'caribbean sauce creole': 'caribbean-sauce-creole',
    'fresh herbal sauce': 'fresh-herbal-sauce',
    'flaming bbq sauce': 'flaming-bbq-sauce',
    'bbq sauce': 'bbq-sauce',
  };
  const sidebarSauceImages = document.querySelectorAll('.ff-sidebar-sauce');
  const handleToData = {};  // { handle: { img, url } }
  sidebarSauceImages.forEach((card) => {
    const link = card.getAttribute('href') || '';
    const img = card.querySelector('img');
    if (!img) return;
    const m = link.match(/\/(?:products|shop)\/([^/?#]+)/);
    if (m) handleToData[m[1]] = { img: img.src, url: link };
  });

  allItems.forEach((li) => {
    const text = li.querySelector('.ff-ing-text');
    if (!text) return;
    const lowered = text.textContent.toLowerCase();
    for (const [name, handle] of Object.entries(sauceLookups)) {
      const data = handleToData[handle];
      if (lowered.includes(name) && data) {
        const re = new RegExp('Full Feast ' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
        const html = text.innerHTML;
        const replaced = html.replace(re, (match) =>
          `<a class="ff-ing-sauce-chip" href="${data.url}"><img src="${data.img}" alt="" loading="lazy">${match}</a>`
        );
        if (replaced !== html) text.innerHTML = replaced;
        break;
      }
    }
  });
})();

/* --------------------------------------------------------------------------
   Jump to recipe. A reader standing in the kitchen with a pan already hot
   wants the ingredients now. Rather than cut JP's story, give them a way past
   it. The button only appears when there is somewhere to jump TO, so culture
   posts never grow a dead control.
   -------------------------------------------------------------------------- */
(function () {
  const root =
    document.querySelector('.ff-article__main .rte') ||
    document.querySelector(".ff-article__main [class*='blog-post-content']");
  if (!root) return;

  const wanted = ['what you', 'ingredients', 'you will need', "you'll need"];
  let target = null;
  for (const h of root.querySelectorAll('h2, h3')) {
    const t = (h.textContent || '').trim().toLowerCase();
    if (wanted.some((w) => t.includes(w))) { target = h; break; }
  }
  if (!target) return;
  if (!target.id) target.id = 'ff-recipe-start';

  const meta = document.querySelector('.ff-article-meta');
  if (!meta) return;

  const btn = document.createElement('a');
  btn.className = 'ff-jump-recipe';
  btn.href = '#' + target.id;
  btn.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>' +
    '<span>' + ((window.FF_I18N || {}).jump_to_recipe || 'Jump to recipe') + '</span>';

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    history.replaceState(null, '', '#' + target.id);
  });

  meta.insertAdjacentElement('afterend', btn);
})();

/* --------------------------------------------------------------------------
   Print card. On paper a cook wants the ingredients and the method, nothing
   else: no story, no FAQ, no tips. When the page prints (the Print button or
   Cmd/Ctrl+P) a one-off card is built from the live page, so the servings the
   reader picked come with it, and article-ff.css hides everything but the card.
   Headings come from the article itself, so a French or German page prints in
   French or German. If the recipe cannot be read, no card is built and the
   older print rules apply unchanged.
   -------------------------------------------------------------------------- */
(function () {
  const root =
    document.querySelector('.ff-article__main .rte') ||
    document.querySelector(".ff-article__main [class*='blog-post-content']");
  if (!root) return;

  const flat = (s) => (s || '').toLowerCase().replace(/[‘’ʼ']/g, '').replace(/\s+/g, ' ').trim();
  const isIngredients = (t) => /youll need|you will need|what you need|ingredient/.test(t);
  const isMethod = (t) => /how (i|to|we) (make|cook)|method|steps|instructions|directions|make it|cook it/.test(t);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* Keep the words and bold lead-ins; drop links, images, buttons, chips. */
  function clean(el) {
    const c = el.cloneNode(true);
    c.querySelectorAll('button, img, svg, script, style').forEach((n) => n.remove());
    c.querySelectorAll('*').forEach((n) => {
      if (/^(STRONG|B|EM|I)$/.test(n.tagName)) [...n.attributes].forEach((a) => n.removeAttribute(a.name));
      else n.replaceWith(...n.childNodes);
    });
    return c.innerHTML.replace(/\s+/g, ' ').trim();
  }

  function sectionAfter(h) {
    const out = [];
    let n = h.nextElementSibling;
    while (n && n.tagName !== 'H2') { out.push(n); n = n.nextElementSibling; }
    return out;
  }

  /* Find the two headings now, while the page is still in English. The
     storefront translator rewrites headings later ("What you'll need" becomes
     "Was Sie benötigen"), so at print time they are found by these marks. */
  (function mark() {
    const h2s = [...root.querySelectorAll('h2')];
    const ingH = h2s.find((h) => isIngredients(flat(h.textContent)));
    if (!ingH) return;
    const after = h2s.slice(h2s.indexOf(ingH) + 1);
    const methH =
      after.find((h) => isMethod(flat(h.textContent))) ||
      after.find((h) => sectionAfter(h).some((e) => e.tagName === 'OL'));
    if (!methH) return;
    ingH.dataset.ffPrint = 'ing';
    methH.dataset.ffPrint = 'method';
  })();

  function read() {
    const ingH = root.querySelector('h2[data-ff-print="ing"]');
    const methH = root.querySelector('h2[data-ff-print="method"]');
    if (!ingH || !methH) return null;

    const ing = [];
    sectionAfter(ingH).forEach((el) => {
      if (/^H[34]$/.test(el.tagName)) ing.push({ group: el.textContent.trim() });
      else if (el.tagName === 'UL' || el.tagName === 'OL') {
        el.querySelectorAll(':scope > li').forEach((li) => {
          ing.push({ html: clean(li.querySelector('.ff-ing-text') || li) });
        });
      }
    });
    if (!ing.some((i) => i.html)) return null;

    /* A numbered list if there is one; otherwise h3 + paragraphs, or paragraphs. */
    const steps = [];
    const sec = sectionAfter(methH);
    if (sec.some((e) => e.tagName === 'OL')) {
      sec.forEach((el) => {
        if (el.tagName === 'OL') el.querySelectorAll(':scope > li').forEach((li) => steps.push({ html: clean(li) }));
        else if (/^H[34]$/.test(el.tagName)) steps.push({ group: el.textContent.trim() });
      });
    } else {
      let cur = null;
      sec.forEach((el) => {
        if (/^H[34]$/.test(el.tagName)) { cur = { title: el.textContent.trim(), html: '' }; steps.push(cur); }
        else if (el.tagName === 'P' && el.textContent.trim()) {
          if (cur) cur.html += (cur.html ? ' ' : '') + clean(el);
          else steps.push({ html: clean(el) });
        }
      });
    }
    if (!steps.some((s) => s.html || s.title)) return null;
    return { ingTitle: ingH.textContent.trim(), methTitle: methH.textContent.trim(), ing, steps };
  }

  function glance() {
    const out = [];
    ['prep', 'cook', 'total', 'serves'].forEach((k) => {
      const row = document.querySelector('.ff-recipe-glance__row[data-glance="' + k + '"]');
      if (!row) return;
      let v = (row.querySelector('dd')?.textContent || '').trim();
      if (k === 'serves') {
        const picked = document.querySelector('.ff-ing-controls [data-servings]');
        if (picked) v = picked.textContent.trim();
      }
      const label = (row.querySelector('dt')?.textContent || '').trim();
      if (v) out.push([label, v]);
    });
    return out;
  }

  /* Pop-ups and app widgets add their own top-level elements, and some win
     against a stylesheet rule, so while printing every top-level element but
     the card is hidden inline, and put back afterwards. */
  let hiddenForPrint = [];
  function restore() {
    hiddenForPrint.forEach(([el, prev]) => {
      if (prev === null) el.style.removeProperty('display');
      else el.style.setProperty('display', prev[0], prev[1]);
    });
    hiddenForPrint = [];
    document.querySelectorAll('.ff-print-card').forEach((n) => n.remove());
    document.body.classList.remove('ff-has-print-card');
    document.documentElement.classList.remove('ff-has-print-card');
  }

  function build() {
    restore();
    const r = read();
    if (!r) return;

    const title =
      (document.querySelector('.ff-article__hero-title')?.innerText || document.title.split('|')[0]).trim();
    const sauces = [...document.querySelectorAll('.ff-sidebar-sauce img')]
      .map((i) => (i.alt || '').replace(/\s[–-]\s.*$/, '').trim()).filter(Boolean);

    /* Steps that already carry their own "1." would print a number twice. */
    const unnumber = (t) => (t || '').replace(/^(<(?:strong|b)>)?\s*\d+[.)]\s*/i, '$1');
    r.steps.forEach((s) => { if (s.title) s.title = unnumber(s.title); if (s.html) s.html = unnumber(s.html); });

    let n = 0;
    const stepsHTML = r.steps.map((s) => {
      if (s.group) return '<li class="ff-pc__group">' + esc(s.group) + '</li>';
      n += 1;
      return '<li class="ff-pc__step"><span class="ff-pc__num">' + n + '</span><div>' +
        (s.title ? '<strong>' + esc(s.title) + '</strong> ' : '') + (s.html || '') + '</div></li>';
    }).join('');

    const card = document.createElement('div');
    card.className = 'ff-print-card';
    card.innerHTML =
      '<header class="ff-pc__top"><span class="ff-pc__brand">FULL FEAST</span>' +
      '<span class="ff-pc__site">fullfeast.com</span></header>' +
      '<h1 class="ff-pc__title">' + esc(title) + '</h1>' +
      '<p class="ff-pc__meta">' + glance().map(([k, v]) => '<span><b>' + esc(k) + '</b> ' + esc(v) + '</span>').join('') +
      (sauces.length ? '<span class="ff-pc__sauce">' + esc(sauces.join(' · ')) + '</span>' : '') + '</p>' +
      '<div class="ff-pc__body">' +
        '<section class="ff-pc__ing"><h2>' + esc(r.ingTitle) + '</h2><ul>' +
          r.ing.map((i) => i.group
            ? '<li class="ff-pc__group">' + esc(i.group) + '</li>'
            : '<li><span class="ff-pc__box"></span><span>' + i.html + '</span></li>').join('') +
        '</ul></section>' +
        '<section class="ff-pc__method"><h2>' + esc(r.methTitle) + '</h2><ol>' + stepsHTML + '</ol></section>' +
      '</div>' +
      /* A div, not <footer>: the older print rules hide every footer element. */
      '<div class="ff-pc__foot">' + esc(location.host + location.pathname) + '</div>';
    document.body.appendChild(card);
    document.body.classList.add('ff-has-print-card');
    document.documentElement.classList.add('ff-has-print-card');
    [...document.body.children].forEach((el) => {
      if (el === card || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(el.tagName)) return;
      const v = el.style.getPropertyValue('display');
      hiddenForPrint.push([el, v ? [v, el.style.getPropertyPriority('display')] : null]);
      el.style.setProperty('display', 'none', 'important');
    });
  }

  window.addEventListener('beforeprint', build);
  window.addEventListener('afterprint', restore);
  /* For checking on screen: FFPrintCard.build() shows only the card, FFPrintCard.restore() undoes it. */
  window.FFPrintCard = { build, restore };
})();
